import { useSocket } from "../../../contexts/SocketContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "../../../components/loading/Loader";
import { InputNumber } from "../../../components/forms/InputNumber";
import { Button } from "../../../components/buttons/Button";
import { PlayCircleIcon } from "@heroicons/react/20/solid";
import { useGetData } from "../../../services/api/api-tools";
import { formatSecondsToMinutesSeconds } from "../../../helpers/date/date-helper";

const LIVE_SETUP_STORAGE_KEY = "lisi_live_selectedWorkOrder_v2";
const SCAN_TIMEOUT_MS = 120;

const OPERATIONS = ['Calibrage', 'Ebauche', 'Finition', 'Aplatissage', 'Répartition', 'Refoulement', 'Formage à chaud'];

const readLiveSetupFromStorage = () => {
  try {
    const raw = localStorage.getItem(LIVE_SETUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeLiveSetupToStorage = (payload) => {
  localStorage.setItem(LIVE_SETUP_STORAGE_KEY, JSON.stringify(payload));
};

const clearLiveSetupFromStorage = () => {
  localStorage.removeItem(LIVE_SETUP_STORAGE_KEY);
};

const getOperationName = (operationId) => {
  return OPERATIONS?.[parseInt(operationId)] ?? 'Unknown operation';
};

const buildProcessName = ({ ofValue, processData }) => {
  const article = processData?.article ?? 'Unknown article';
  const operation = processData?.operation ?? 'Unknown operation';
  return `#${ofValue} - ${article} (${operation})`;
};

const buildPatternPayloadFromProcessData = (processData) => ({
  name: `${processData.article} [${processData.operation}]`,
  workstation_id: 1,
  pattern_data: [
    { name: 'oven_time', value: processData.oven_time ?? 0 },
    { name: 'oven_tolerance', value: processData.oven_tolerance ?? 0 },
    { name: 'transfer_time', value: processData.transfer_time ?? 0 },
    { name: 'transfer_tolerance', value: processData.transfer_tolerance ?? 0 },
  ],
});

const parseWorkOrderFromBarcode = (value) => {
  const normalized = String(value ?? '').trim();
  if (normalized.length < 6) return null;
  return normalized.slice(-6);
};

const parseDatamatrix = (value) => {
  const normalized = String(value ?? '').trim();

  if (!normalized) return null;

  const parts = normalized.split(';').map((part) => part.trim());

  if (parts.length < 40) return null;

  const article = String(parts[1] ?? '').trim();
  const operation = String(getOperationName(parts[2]) ?? '').trim();

  if (!article || !operation) return null;

  return {
    article,
    operation,
    oven_time: parseInt(parts[7]) * 60,
    oven_tolerance: (parseInt(parts[9]) - parseInt(parts[7])) * 60, // oven_time_max - oven_time
    transfer_time: parseInt(parts[36]),
    transfer_tolerance: parseInt(parts[38]) - parseInt(parts[36]), // transfer_time_max - transfer_time
  };
};

const savePatternFromProcessData = async (processData) => {

  const payload = [buildPatternPayloadFromProcessData(processData)];

  const response = await fetch(`${process.env.REACT_APP_API_ADDRESS}patterns/list`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok)
    throw new Error('Pattern save failed');

  const created = await response.json();
  const pattern = Array.isArray(created) ? created[0] : created;

  return pattern?.id ?? null;
};

export const LISILiveSetupPageV2 = () => {
  const { sendEvent } = useSocket();

  const [quantity, setQuantity] = useState('');
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [step, setStep] = useState(1);

  const [datamatrixInput, setDatamatrixInput] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [processData, setProcessData] = useState(null);
  const [ofValue, setOfValue] = useState('');
  const [scanError, setScanError] = useState('');

  const datamatrixTimeoutRef = useRef(null);
  const barcodeTimeoutRef = useRef(null);
  const startDate = useRef(null);
  const datamatrixInputRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const datamatrixScanLockRef = useRef(false);
  const barcodeScanLockRef = useRef(false);

  useEffect(() => {
    if (selectedWorkOrder) return;

    const saved = readLiveSetupFromStorage();
    if (!saved?.selectedWorkOrder) return;

    setQuantity(saved?.quantity ?? 0);
    setProcessData(saved?.processData ?? null);
    setOfValue(saved?.ofValue ?? '');
    setSelectedWorkOrder(saved.selectedWorkOrder);
    setStep(3);
    startDate.current = saved?.startDate ?? null;

    sendEvent('current_data', { 'start_production': saved.selectedWorkOrder });
  }, [selectedWorkOrder, sendEvent]);

  const focusActiveInput = () => {
    if (selectedWorkOrder) return;

    const activeInput = step === 1 ? datamatrixInputRef.current : step === 2 ? barcodeInputRef.current : null;

    activeInput?.focus();
    activeInput?.select?.();
  };

  useEffect(() => {
    focusActiveInput();
  }, [step, selectedWorkOrder]);

  useEffect(() => {
    const handleWindowClick = () => {
      focusActiveInput();
    };

    window.addEventListener('click', handleWindowClick);

    return () => {
      window.removeEventListener('click', handleWindowClick);
      window.clearTimeout(datamatrixTimeoutRef.current);
      window.clearTimeout(barcodeTimeoutRef.current);
    };
  }, [step, selectedWorkOrder]);

  const resetSetup = () => {
    setStep(1);
    setDatamatrixInput('');
    setBarcodeInput('');
    setProcessData(null);
    setOfValue('');
    setQuantity('');
    setScanError('');
  };

  const finalizeDatamatrixScan = () => {
    window.clearTimeout(datamatrixTimeoutRef.current);
    datamatrixTimeoutRef.current = null;

    const parsed = parseDatamatrix(datamatrixInput);

    if (!parsed) {
      setScanError('Datamatrix invalide ou non reconnu.');
      setDatamatrixInput('');
      return;
    }

    setProcessData(parsed);
    setScanError('');
    setDatamatrixInput('');
    setStep(2);
  };

  const finalizeBarcodeScan = () => {
    if (barcodeScanLockRef.current) return;
    barcodeScanLockRef.current = true;

    window.clearTimeout(barcodeTimeoutRef.current);
    barcodeTimeoutRef.current = null;

    const parsedOf = parseWorkOrderFromBarcode(barcodeInput);

    if (!parsedOf) {
      setScanError('Code-barres invalide.');
      setBarcodeInput('');
      barcodeScanLockRef.current = false;
      return;
    }

    setOfValue(parsedOf);
    setScanError('');
    setBarcodeInput('');
    setStep(3);
    barcodeScanLockRef.current = false;
  };

  const scheduleDatamatrixFinalize = () => {
    window.clearTimeout(datamatrixTimeoutRef.current);
    datamatrixTimeoutRef.current = window.setTimeout(() => {
      finalizeDatamatrixScan();
    }, SCAN_TIMEOUT_MS);
  };

  const scheduleBarcodeFinalize = () => {
    window.clearTimeout(barcodeTimeoutRef.current);
    barcodeTimeoutRef.current = window.setTimeout(() => {
      finalizeBarcodeScan();
    }, SCAN_TIMEOUT_MS);
  };

  const handleDatamatrixKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      finalizeDatamatrixScan();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
    }
  };

  const handleBarcodeKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      finalizeBarcodeScan();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
    }
  };

  const handleDatamatrixChange = (value) => {
    setDatamatrixInput(value);
    setScanError('');

    if (String(value ?? '').trim()) {
      scheduleDatamatrixFinalize();
    }
  };

  const handleBarcodeChange = (value) => {
    setBarcodeInput(value);
    setScanError('');

    if (String(value ?? '').trim()) {
      scheduleBarcodeFinalize();
    }
  };

  const handleStart = async () => {
    if (!processData || !ofValue || !(quantity > 0)) return;

    let savedPatternId = null;

    try {
      savedPatternId = await savePatternFromProcessData(processData);
    } catch {
      window.alert('Erreur lors de l’enregistrement du pattern.');
      return;
    }

    if (!savedPatternId) {
      window.alert('Impossible de récupérer l\'id du pattern créé.');
      return;
    }

    const nextProcessData = {
      ...processData,
      pattern_id: savedPatternId,
      of: ofValue,
      quantity,
      name: buildProcessName({ ofValue, processData }),
    };

    const startTs = Math.floor(Date.now() / 1000);

    writeLiveSetupToStorage({
      startDate: startTs,
      processData: nextProcessData,
      ofValue,
      quantity,
      selectedWorkOrder: nextProcessData,
    });

    setProcessData(nextProcessData);
    sendEvent('current_data', { 'start_production': nextProcessData });
    setSelectedWorkOrder(nextProcessData);
    startDate.current = startTs;
  };

  const handleStop = async () => {
    await fetch(`${process.env.REACT_APP_API_ADDRESS}pattern_executions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        pattern: `/api/patterns/${selectedWorkOrder.pattern_id}`,
        start_date: startDate.current,
        stop_date: Math.floor(Date.now() / 1000),
        data: {
          of: ofValue,
          quantity
        }
      }),
    });

    clearLiveSetupFromStorage();

    sendEvent('current_data', { 'stop_production': null });
    setSelectedWorkOrder(null);
    startDate.current = null;
    resetSetup();
  };

  return (
    <div className="h-full bg-gray-100 flex flex-col gap-16 justify-center items-center text-center text-xl text-perception-gray-700">
      {
        !selectedWorkOrder ?
          <div className="flex flex-col justify-center gap-16 h-full">
            <div className="flex flex-col items-center gap-8 p-8 pb-12 rounded-md bg-white min-w-[900px]">
              <h1 className="font-semibold text-perception-blue">Démarrer une production</h1>

              <div className="flex items-center gap-4">
                <div className={`px-4 py-2 rounded-md ${step === 1 ? 'bg-perception-blue text-white' : 'bg-gray-200'}`}>
                  1. Scanner le datamatrix
                </div>
                <div className={`px-4 py-2 rounded-md ${step === 2 ? 'bg-perception-blue text-white' : 'bg-gray-200'}`}>
                  2. Scanner le code-barres OF
                </div>
                <div className={`px-4 py-2 rounded-md ${step === 3 ? 'bg-perception-blue text-white' : 'bg-gray-200'}`}>
                  3. Saisir la quantité
                </div>
              </div>

              {step === 1 && (
                <div className="absolute top-0 left-0 opacity-0 pointer-events-none">
                  <input
                    ref={datamatrixInputRef}
                    type='text'
                    value={datamatrixInput}
                    onChange={(event) => handleDatamatrixChange(event.target.value)}
                    placeholder='Scannez le datamatrix'
                    onKeyDown={handleDatamatrixKeyDown}
                    onFocus={(event) => event.target.select()}
                    autoFocus
                  />
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col gap-6 w-full max-w-2xl">
                  <div className="rounded-md bg-gray-50 p-4 text-left text-base">
                    <p><span className="font-semibold">Article :</span> {processData?.article ?? '—'}</p>
                    <p><span className="font-semibold">Opération :</span> {processData?.operation ?? '—'}</p>
                    <p><span className="font-semibold">Temps de chauffe :</span> {formatSecondsToMinutesSeconds(processData?.oven_time) ?? '—'}</p>
                    <p><span className="font-semibold">Tolérance temps de chauffe :</span> {formatSecondsToMinutesSeconds(processData?.oven_tolerance) ?? '—'}</p>
                    <p><span className="font-semibold">Temps de transfert :</span> {formatSecondsToMinutesSeconds(processData?.transfer_time) ?? '—'}</p>
                    <p><span className="font-semibold">Tolérance temps de transfert :</span> {formatSecondsToMinutesSeconds(processData?.transfer_tolerance) ?? '—'}</p>
                  </div>

                  <div className="absolute top-0 left-0 opacity-0 pointer-events-none">
                    <input
                      ref={barcodeInputRef}
                      type='text'
                      value={barcodeInput}
                      onChange={(event) => handleBarcodeChange(event.target.value)}
                      placeholder='Scannez le code-barres OF'
                      onKeyDown={handleBarcodeKeyDown}
                      onFocus={(event) => event.target.select()}
                      autoFocus
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="flex flex-col gap-6 w-full max-w-2xl">
                  <div className="rounded-md bg-gray-50 p-4 text-left text-base">
                    <p><span className="font-semibold">Article :</span> {processData?.article ?? '—'}</p>
                    <p><span className="font-semibold">Opération :</span> {processData?.operation ?? '—'}</p>
                    <p><span className="font-semibold">OF :</span> {ofValue || '—'}</p>
                  </div>

                  <div className="flex items-center justify-center gap-4">
                    Quantité : <InputNumber value={quantity} setValue={setQuantity} size="lg" />
                  </div>

                  <div className="flex items-center justify-center gap-4">
                    <Button type='button' category='secondary-btn' size='small' onClick={resetSetup}>
                      Recommencer
                    </Button>

                    <Button type='submit' category='primary-btn' size='small' disabled={!(quantity > 0)} onClick={handleStart}>
                      Démarrer la production
                    </Button>
                  </div>
                </div>
              )}

              {scanError && (
                <div className="text-red-600 text-base font-medium">
                  {scanError}
                </div>
              )}
            </div>
          </div>
          :
          <div className="flex flex-col items-center gap-16">
            <PlayCircleIcon className="size-64 text-perception-gray-500"/>
            <div>
              <p>Une production est en cours :</p>
              <p className="text-xl text-perception-blue">{selectedWorkOrder.name} (Quantité : {selectedWorkOrder.quantity})</p>
            </div>
            <Button type='submit' category='primary-btn' size='small' onClick={handleStop} className="w-64">
              Arrêter la production
            </Button>
          </div>
      }
    </div>
  );
};